/**
 * @voltxampere/web — Bookmark store (Sprint 32-3)
 *
 * Per-circuit bookmarks for the time scrubber. Users can pin a `t`
 * with a free-form note ("Bu anda ne oldu?" / "What happened here?"),
 * up to 280 characters per pin. Bookmarks survive page reloads (IDB
 * persistence in Sprint 32-4) and follow the active circuit (preset
 * switch loads that circuit's bookmark set).
 *
 * Sprint 32-3 ships the in-memory store + UI helpers. Sprint 32-4
 * wires IDB persistence on top — this module's mutators stay
 * synchronous and the persistence layer subscribes to the signal.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal, type ReadonlySignal } from "@preact/signals-core";

import { currentCircuit } from "./circuit.js";
import { playbackSpeedSignal, setPlaybackSpeed } from "./playback-speed.js";

/** Maximum note length per bookmark (characters, not bytes). */
export const BOOKMARK_NOTE_MAX_LENGTH = 280;

/** Minimum scrubber t (seconds) — bookmarks at t < 0 rejected. */
const MIN_BOOKMARK_T = 0;

export interface Bookmark {
  readonly id: string;
  readonly circuitId: string;
  /** Time in seconds where the pin sits on the scrubber track. */
  readonly t: number;
  /** Free-form note (max 280 chars, sliced silently). */
  readonly note: string;
  /** Unix milliseconds — for stable sort + audit. */
  readonly createdAt: number;
  /**
   * Sprint 53 — playback speed multiplier captured at addBookmark
   * time (from playbackSpeedSignal). On `restorePlaybackSpeedFromBookmark`
   * the active speed is reset to this value. Undefined for pre-S53
   * bookmarks or when the speed snapshot is intentionally skipped.
   */
  readonly speed?: number;
}

/**
 * All bookmarks across all circuits — Sprint 32-4 IDB load populates
 * this once at boot and per-preset-switch. UI filters via
 * `currentCircuitBookmarks` so the user only sees the active
 * circuit's pins.
 */
export const bookmarksSignal = signal<readonly Bookmark[]>([]);

/** Bookmarks for the active circuit, sorted by t ascending. */
export const currentCircuitBookmarks: ReadonlySignal<readonly Bookmark[]> = computed(() => {
  const circuit = currentCircuit.value;
  if (!circuit) return [];
  return bookmarksSignal.value
    .filter((b) => b.circuitId === circuit.id)
    .slice()
    .sort((a, b) => a.t - b.t);
});

/** Generate a stable id for a new bookmark. */
function makeBookmarkId(): string {
  return `bk_${String(Date.now())}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Add a bookmark at the given t for the active circuit. Returns the
 * created bookmark (or null if no active circuit / invalid t).
 *
 * Note is silently sliced to BOOKMARK_NOTE_MAX_LENGTH.
 */
export function addBookmark(t: number, note: string): Bookmark | null {
  const circuit = currentCircuit.value;
  if (!circuit) return null;
  if (!Number.isFinite(t) || t < MIN_BOOKMARK_T) return null;
  const bookmark: Bookmark = {
    id: makeBookmarkId(),
    circuitId: circuit.id,
    t,
    note: note.slice(0, BOOKMARK_NOTE_MAX_LENGTH),
    createdAt: Date.now(),
    // Sprint 53 — snapshot the active playback speed so future jumps
    // can restore it. Always set, even when speed === 1, so the
    // bookmark is self-describing across schema migrations.
    speed: playbackSpeedSignal.value,
  };
  bookmarksSignal.value = [...bookmarksSignal.value, bookmark];
  return bookmark;
}

/**
 * Sprint 53 — restore the playback speed captured in `bookmark.speed`,
 * if any. Called by the scrubber's "jump to bookmark" handler so the
 * user reads back to the same speed they were watching at when they
 * pinned the moment. No-op when the bookmark has no speed snapshot
 * (pre-S53 records) or when the snapshot equals the active speed.
 *
 * Returns true when a restore happened, false otherwise — callers
 * can surface a toast with `speed.bookmark.restored` on `true`.
 */
export function restorePlaybackSpeedFromBookmark(bookmark: Bookmark): boolean {
  if (typeof bookmark.speed !== "number") return false;
  if (bookmark.speed === playbackSpeedSignal.value) return false;
  setPlaybackSpeed(bookmark.speed);
  return true;
}

/** Remove a bookmark by id. No-op when id not found. */
export function removeBookmark(id: string): void {
  bookmarksSignal.value = bookmarksSignal.value.filter((b) => b.id !== id);
}

/**
 * Update the note text of an existing bookmark. Note is silently
 * sliced to BOOKMARK_NOTE_MAX_LENGTH. No-op when id not found.
 */
export function updateBookmarkNote(id: string, note: string): void {
  bookmarksSignal.value = bookmarksSignal.value.map((b) =>
    b.id === id ? { ...b, note: note.slice(0, BOOKMARK_NOTE_MAX_LENGTH) } : b,
  );
}

/**
 * Replace the in-memory bookmark set wholesale. Sprint 32-4 IDB
 * hydrate uses this — it loads all-circuits bookmarks into memory
 * once at boot.
 */
export function hydrateBookmarks(bookmarks: readonly Bookmark[]): void {
  bookmarksSignal.value = [...bookmarks];
}

/** Test cleanup. */
export function __resetBookmarksForTests(): void {
  bookmarksSignal.value = [];
}
